import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Shown above the reload button — keep it specific to what crashed. */
  fallbackTitle?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time exceptions in its subtree (e.g. a query resolving with
 * an unexpectedly-shaped payload) and shows a recoverable error message
 * instead of unmounting the whole tree to a blank white screen — React's own
 * dev-console warning for an uncaught render error recommends exactly this.
 *
 * Must be a class component; there is no hook equivalent for
 * getDerivedStateFromError/componentDidCatch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="mx-auto mt-6 flex max-w-md items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="text-sm text-red-800">
          <p className="font-medium">{this.props.fallbackTitle ?? 'Something went wrong'}</p>
          <p className="mt-0.5 text-red-700">{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
